import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useUI } from '@components/ui'
import { API_STATUS, EmptyGuid } from '@components/utils/constants'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import useSection from 'hooks/useSection'
import { NextRouter, useRouter } from 'next/router'
import {
  MEN_COLLECTIONS_ACTION_TYPES,
  menCollectionsReducer,
  menCollectionsStateInterface,
} from '../helpers/reducer'
import {
  IProductResponseWarperWithResultObj,
  IProductResponseWithResultArray,
} from '../helpers/productResponse.model'
import { defaultYOffset, pageView, transformProductResult } from '../helpers'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { getCurrentPage } from '@framework/utils/app-util'
import { ISortItem } from '@components/product/Filters/CollectionMobileFilter'
import { IBannerData } from '@components/utils/IntersectionObserverComponent'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import {
  IProductsPayload,
  ProductService,
} from 'services/product/index.service'
import {
  parseUrlToFilterForPlp,
  getSortValueForPlp,
  removeValueFromQueryParam,
  updateFilterToQueryParams,
  applyMultipleFilterToQueryParams,
  clearFilterFromQuery,
  sortWithQueryParam,
} from '@components/search/helper'
import { getItem } from '@components/utils/localStorage'
import { LocalStorage } from '@components/utils/payment-constants'
import Cookies from 'js-cookie'

const IS_INFINITE_SCROLL =
  process.env.NEXT_PUBLIC_ENABLE_INFINITE_SCROLL === 'true'

const DEFAULT_STATE = {
  sortBy: '',
  sortOrder: 'asc',
  currentPage: 1,
  filters: [],
  collectionId: '',
}

interface IProps {
  id: string
  previousPath: string
  AF: any
  window: any
  customLinks: any
  images: any
  router: NextRouter
  products: IProductResponseWithResultArray
}

const useMenCollection = ({
  id,
  products,
  customLinks,
  AF,
  images,
  window,
  router,
}: IProps) => {
  const {
    setAppliedFilters: setAppliedFiltersState,
    appliedFilters: appliedFiltersState,
    user,
  } = useUI()
  const trackMoEngageEvent = useMoEngageEvent()
  const [swrLoading, setSwrLoading] = useState<API_STATUS>(
    API_STATUS.NOT_STARTED
  )

  let lastScrollTop = 0
  const adaptedQuery: any = useMemo(() => ({ ...router.query }), [router.query])

  adaptedQuery.currentPage
    ? (adaptedQuery.currentPage = Number(adaptedQuery.currentPage))
    : false
  adaptedQuery.filters
    ? (adaptedQuery.filters = JSON.parse(adaptedQuery.filters))
    : false

  const queryParams = new URLSearchParams(router.asPath.split(/\?/)[1] || '')
  const filterResult = parseUrlToFilterForPlp(
    queryParams.toString(),
    products?.filters
  )
  const sortResult = getSortValueForPlp(queryParams.toString())

  const initialState = {
    ...DEFAULT_STATE,
    filters: filterResult || [],
    sortBy: sortResult || '',
    collectionId: id,
  }

  const [state, dispatch] = useReducer(menCollectionsReducer, initialState)

  const [colId, setColId] = useState<string | null>(null)
  const [initialRender, setInitialRender] = useState(true)
  const [filtersLoader, setFiltersLoader] = useState<API_STATUS>(
    API_STATUS.NOT_STARTED
  )
  const [quickViewProductSlug, setQuickViewProductSlug] = useState<
    string | null
  >(null)

  const filterKeys = [
    'category',
    'colour',
    'neck',
    'pattern',
    'size',
    'closure',
    'single/pack',
  ]

  const { SORT_BY, PAGE, CLEAR, UPDATE_COLLECTION_ID, SET_FILTERS } =
    MEN_COLLECTIONS_ACTION_TYPES

  useEffect(() => {
    const queryParams = new URLSearchParams(router.asPath.split(/\?/)[1] || '')
    const filterResult = parseUrlToFilterForPlp(
      queryParams.toString(),
      products?.filters
    )
    const sortResult = getSortValueForPlp(queryParams.toString())
    dispatch({
      type: SET_FILTERS,
      payload: filterResult,
    })
    dispatch({
      type: SORT_BY,
      payload: sortResult,
    })
  }, [router.asPath])

  const initialProductListMemory: IProductResponseWarperWithResultObj = useMemo(
    () => ({
      products: {
        results: {},
        sortList: [],
        pages: 0,
        total: 0,
        totalProductRendered: 0,
        currentPage: 1,
        filters: [],
        sortBy: '',
        collectionId: id,
      },
    }),
    [id]
  )

  const [productListMemory, setProductListMemory] = useState(
    initialProductListMemory
  )
  const [position, setPosition] = useState(defaultYOffset(window))
  const [visible, setVisible] = useState(true)
  const [appliedFilters, setAppliedFilters] = useState<any[]>([])

  useEffect(() => {
    pageView(user)
    if (!!getItem(LocalStorage.Key.IS_OFFLINE_STORE)) {
      Cookies.set(LocalStorage.Key.IS_OFFLINE_STORE, 'false')
    }
    return () => {
      setAppliedFiltersState([])
    }
  }, [setAppliedFiltersState, user])

  const productDataToPass = IS_INFINITE_SCROLL
    ? transformProductResult(productListMemory)?.products
    : products

  const handleAPIStatus = useCallback((apiStatus: API_STATUS) => {
    setSwrLoading(apiStatus)
  }, [])

  const handleResponse = (res: any) => {
    if (
      // res?.products?.currentPage !== productListMemory?.products?.currentPage ||
      res?.products?.currentPage >= productListMemory?.products?.currentPage ||
      res?.products?.currentPage <= productListMemory?.products?.currentPage ||
      res?.products?.pages !== productListMemory?.products?.pages ||
      res?.products?.sortBy !== productListMemory?.products?.sortBy ||
      res?.products?.total !== productListMemory?.products?.total
    ) {
      setProductListMemory((prevData: any) => {
        let dataClone = {
          ...res,
          products: {
            ...res.products,
            currentPage:
              res?.products?.currentPage &&
              res.products.currentPage > prevData?.products?.currentPage
                ? res.products.currentPage
                : prevData?.products?.currentPage,
            totalProductRendered:
              prevData?.products?.totalProductRendered +
                res?.products?.results?.length ?? 0,
            results: {
              [res?.products?.currentPage ?? 1]: res?.products?.results?.map(
                (datum: any) => ({
                  ...datum,
                  currentPage: res?.products?.currentPage ?? 1,
                })
              ),
            },
          },
        }
        if (!dataClone?.products || !dataClone?.products?.results) {
          dataClone = {
            products: {
              results: {},
            },
          }
        }

        if (state.currentPage > 1) {
          dataClone.products.results = {
            ...prevData.products.results,
            ...dataClone.products.results,
          }
        } else {
          dataClone.products.currentPage = res?.products?.currentPage ?? 1
          dataClone.products.totalProductRendered =
            res?.products?.results?.length ?? 0
        }
        return dataClone
      })
      // setProductListMemory((prevData: any) => {
      //   let dataClone = {
      //     ...res,
      //     products: {
      //       ...res.products,
      //       results: res.products.results.map((datum: any) => ({
      //         ...datum,
      //         currentPage: res?.products?.currentPage,
      //       })),
      //     },
      //   }
      //   if (!dataClone.products || !dataClone.products.results) {
      //     dataClone = {
      //       products: {
      //         results: [],
      //       },
      //     }
      //   }

      //   if (state.currentPage > 1) {
      //     dataClone.products.results = [
      //       ...prevData.products.results,
      //       ...dataClone.products.results,
      //     ]
      //   }
      //   return dataClone
      // })
    }
  }

  const getProductListData = async (params: menCollectionsStateInterface) => {
    try {
      setFiltersLoader(API_STATUS.LOADING)
      handleAPIStatus(API_STATUS.LOADING)
      const query: IProductsPayload = {
        currentPage: params.currentPage
          ? Number(params.currentPage)
          : undefined,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        filters: params.filters,
        collectionId: id,
      }
      const res = await ProductService.retrieveProducts(query)
      await handleResponse(res?.data?.products)
      await handleAPIStatus(API_STATUS.SUCCESS)
      await setFiltersLoader(API_STATUS.SUCCESS)
      return res
    } catch (err) {
      setFiltersLoader(API_STATUS.FAILURE)
      handleAPIStatus(API_STATUS.FAILURE)
      return null
    }
  }
  const checkKeysInRoute = () => {
    const queryParams = new URLSearchParams(window.location.search)
    const allParams: Record<string, string> = {}

    for (const [key, value] of queryParams.entries()) {
      allParams[key] = value
    }
    const query = Object.fromEntries(
      Object.entries(allParams).map(([key, value]) => [
        key.toLowerCase(),
        value,
      ])
    )
    for (const key in query) {
      if (filterKeys?.includes(key)) {
        return true
      }
    }

    return false
  }

  useEffect(() => {
    if (IS_INFINITE_SCROLL) {
      const isChecked = checkKeysInRoute()
      if (isChecked && state?.filters?.length > 0) {
        getProductListData(state)
      } else if (!isChecked) {
        getProductListData(state)
      }
    }
  }, [
    state.currentPage,
    state.filters?.length,
    state.sortBy,
    state.sortOrder,
    router?.query?.collection?.[0],
  ])

  useEffect(() => {
    if (colId != id) {
      setColId(id)
    }
    setProductListMemory({
      products: {
        ...products,
        totalProductRendered: products?.results?.length ?? 0,
        results: !!products?.results?.length
          ? {
              1: products?.results?.map((datum: any) => ({
                ...datum,
                currentPage: products?.currentPage,
              })),
            }
          : {},
      },
    })
  }, [products?.filters, products])

  /**
   * Below useEffect will run whenever the collection ID will change
   */
  useEffect(() => {
    // // clear filters
    // dispatch({ type: CLEAR })
    // // clear sorting
    // dispatch({
    //   type: SORT_BY,
    //   payload: '',
    // })

    // update collection ID
    dispatch({
      type: UPDATE_COLLECTION_ID,
      payload: id,
    })
  }, [id])

  useEffect(() => {
    if (!!appliedFiltersState?.length && initialRender) {
      dispatch({
        type: SET_FILTERS,
        payload: appliedFiltersState,
      })
    }
    setInitialRender(false)
  }, [initialRender, appliedFiltersState])

  useEffect(() => {
    if (adaptedQuery && adaptedQuery.sortBy && initialRender) {
      handleSortBy(adaptedQuery.sortBy)
    }
    setInitialRender(false)
  }, [adaptedQuery])

  useEffect(() => {
    const currentFilters = products?.filters?.reduce(
      (acc: any, obj: any) => {
        acc.forEach((item: any) => {
          if (item.Key === obj.key) {
            item['name'] = obj.name
            return item
          }
          return acc
        })
        return acc
      },
      [...state?.filters]
    )
    setAppliedFilters(currentFilters)
  }, [state?.filters, products])

  const handlePageChange = (page: any) => {
    if (productListMemory?.products?.pages > page.selected) {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, currentPage: page.selected + 1 },
        },
        undefined,
        { shallow: true }
      )
      // dispatch({ type: PAGE, payload: page.selected + 1 })
      if (typeof window !== 'undefined') {
        window?.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth',
        })
      }
    }
  }

  const handleInfiniteScroll = () => {
    if (
      productListMemory.products.pages &&
      state.currentPage < productListMemory.products.pages
    ) {
      dispatch({ type: PAGE, payload: state.currentPage + 1 })
    }
  }

  const removeKeysFromRoute = (filter: any) => {
    if (checkKeysInRoute()) {
      const { pathname, query } = router
      for (const key in query) {
        if (filter && query[key] == filter['Value']) {
          delete query[key]
          router.replace(
            {
              pathname,
              query,
            },
            undefined,
            { shallow: true }
          )
        }
      }
    }
  }

  const handleFilters = (filter: any, type: string) => {
    if (type == MEN_COLLECTIONS_ACTION_TYPES.ADD_FILTERS) {
      updateFilterToQueryParams(`${filter?.name}.filter`, filter?.Value, router)
    } else if (type == MEN_COLLECTIONS_ACTION_TYPES.REMOVE_FILTERS) {
      removeValueFromQueryParam(`${filter?.name}.filter`, filter?.Value, router)
    }

    let filter_removed = ''
    const filterKeys = filter?.Key?.split('.')
    const filterName = filterKeys.length
      ? filterKeys[filterKeys.length - 1]
      : ''
    const filter_applied = filterName ? [{ filterName, ...filter }] : []
    if (type == MEN_COLLECTIONS_ACTION_TYPES.REMOVE_FILTERS) {
      removeKeysFromRoute(filter)
    }
    appliedFilters.forEach((datum) => {
      const datumKeys = datum?.Key?.split('.')
      const datumName = datumKeys.length ? datumKeys[datumKeys.length - 1] : ''
      if (
        !filter_applied.find(
          (datumFilter) => datumFilter.filterName === datumName
        )
      ) {
        filter_applied.push({ filterName: datumName, ...datum })
      }
    })
    if (
      appliedFilters.find((datumFilter) => datumFilter.Value === filter.Value)
    ) {
      filter_removed = filter.Value
      const index = filter_applied.findIndex(
        (datumFilter) => datumFilter.Value === filter.Value
      )
      filter_applied.splice(index, 1)
    }
    const sort_and_filter = {
      action: 'filter',
      filter_applied: filter_applied.map((datum) => datum.Value).join(),
      filter_removed,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    }
    recordGA4Event(window, GA_EVENT.SORT_AND_FILTER, {
      action: 'filter',
      filter_applied: filter_applied.map((datum) => datum.filterName).join(),
    })
  }

  const handleFiltersApply = (
    filters: any,
    sortItem?: ISortItem,
    headerName?: string
  ) => {
    const filter_applied: string[] = []
    if (filters?.length > 0 || sortItem?.key !== '') {
      applyMultipleFilterToQueryParams(filters, router, sortItem)
    } else {
      clearFilterFromQuery(router)
    }
    filters.forEach((datum: any) => {
      if (!filter_applied.includes(datum.name)) {
        const filterKeys = datum?.Key?.split('.')
        const filterName = filterKeys.length
          ? filterKeys[filterKeys.length - 1]
          : ''
        filter_applied.push(filterName)
      }
    })
    if (sortItem?.eventValue) {
      filter_applied.push(sortItem.eventValue)
    }
    recordGA4Event(window, GA_EVENT.SORT_AND_FILTER, {
      action: 'apply',
      header: headerName,
      filter_applied: filter_applied.join(),
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })
    dispatch({
      type: SET_FILTERS,
      payload: filters,
    })
    dispatch({ type: PAGE, payload: 1 })
  }

  const handleSortBy = (payload: any) => {
    sortWithQueryParam(payload, router)
  }

  const clearAll = () => {
    clearFilterFromQuery(router)
    recordGA4Event(window, GA_EVENT.SORT_AND_FILTER, {
      action: 'filter',
      filter_applied: '',
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })
    // setAppliedFiltersState([])
    dispatch({ type: CLEAR })
  }

  const handleScroll = () => {
    if (typeof window !== 'undefined') {
      const categoriesRail =
        window.document.getElementById('categories-rail') || null
      const filtersRail = window.document.getElementById('filters-rail') || null

      let currentScroll =
        window.pageYOffset || document.documentElement.scrollTop

      let moving = window?.pageYOffset

      if (customLinks?.length > 0) {
        if (currentScroll > lastScrollTop) {
          // Scroll down
          if (filtersRail) {
            filtersRail.classList.remove('activePLPFiltersRail')
          }
        } else if (currentScroll < lastScrollTop) {
          // Scroll up
          if (filtersRail) {
            if (moving - 50 >= window.innerHeight) {
              filtersRail.classList.add('activePLPFiltersRail')
            } else {
              filtersRail.classList.remove('activePLPFiltersRail')
            }
          }
        } else {
          if (filtersRail) {
            filtersRail.classList.remove('activePLPFiltersRail')
          }
        }
        lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
      } else {
        if (filtersRail) {
          filtersRail.classList.remove('activePLPFiltersRail')
        }
      }
      setVisible(position > moving)
      setPosition(moving)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window?.addEventListener('scroll', handleScroll, { passive: true })
    }
    return () => {
      window?.removeEventListener('scroll', handleScroll)
    }
  }, [customLinks?.length])

  const hasAllItemsLoaded =
    productListMemory?.products?.totalProductRendered &&
    productListMemory?.products?.totalProductRendered >=
      productListMemory?.products?.total

  const totalResults =
    appliedFilters?.length > 0
      ? productListMemory?.products?.total
      : products?.total || productListMemory?.products?.totalProductRendered

  const cls = visible
    ? 'sticky w-full mx-auto bg-white top-108 sm:container'
    : 'relative'

  return {
    totalResults,
    cls,
    hasAllItemsLoaded,
    swrLoading,
    state,
    productDataToPass,
    productListMemory,
    quickViewProductSlug,
    filtersLoader,
    setFiltersLoader,
    setQuickViewProductSlug,
    handlePageChange,
    handleInfiniteScroll,
    handleFilters,
    handleFiltersApply,
    handleSortBy,
    clearAll,
  }
}

export { useMenCollection }
